import { motion, useInView, animate } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { Trophy, Medal, Quote } from 'lucide-react';

/**
 * Champions / Ambassadeurs section
 *
 * Showcases elite shooters who endorse and recommend ClayPulse. The first
 * ambassador is Patrick Béasse — European Champion in Olympic Skeet with a
 * world record of 196/200 at the 1989 UIT European Championship in Zagreb.
 *
 * Adding a new champion: just append to the `champions` array below.
 */

const champions = [
  {
    id: 'patrick-beasse',
    name: 'Patrick Béasse',
    country: 'France',
    discipline: 'Skeet Olympique',
    titles: ['Champion d’Europe', 'Record du Monde 196/200'],
    record: { value: 196, total: 200 },
    year: 1989,
    event: 'UIT European Championship · Zagreb',
    photo:
      '/assets/img/misc-1.png',
    diploma:
      '/assets/img/misc-2.png',
    quote:
      'ClayPulse est l’outil que j’aurais rêvé d’avoir durant toute ma carrière. Pour s’entraîner sérieusement au Skeet Olympique, analyser ses séries, comprendre ses progrès — je le recommande sans hésiter.',
  },
];

function BigCounter({ target, total }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration: 2.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setValue(Math.floor(v)),
    });
    return () => controls.stop();
  }, [isInView, target]);

  return (
    <span
      ref={ref}
      className="font-['Poppins'] font-black tabular-nums tracking-tight"
    >
      <span className="bg-gradient-to-br from-[#FF6B35] via-[#FF8F4F] to-[#FF6B35] bg-clip-text text-transparent text-6xl sm:text-7xl lg:text-8xl leading-none">
        {value}
      </span>
      <span className="text-white/30 text-3xl sm:text-4xl lg:text-5xl font-bold">
        /{total}
      </span>
    </span>
  );
}

function ChampionCard({ champion, index }) {
  return (
    <div
      data-testid={`champion-${champion.id}`}
      className="relative group"
    >
      {/* Ambient glow behind card */}
      <div
        aria-hidden
        className="absolute -inset-x-20 -inset-y-16 bg-[radial-gradient(ellipse_at_center,rgba(255,107,53,0.12),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
      />

      <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
        {/* LEFT: Photo + Diploma stack */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative order-2 lg:order-1 min-h-[560px] lg:min-h-[640px] flex items-center justify-center"
        >
          {/* Decorative orbital rings */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none overflow-hidden"
          >
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] lg:w-[520px] lg:h-[520px] rounded-full border border-white/[0.06]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] lg:w-[420px] lg:h-[420px] rounded-full border border-[#FF6B35]/[0.08]" />
          </div>

          {/* Grid dots in background */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage: 'radial-gradient(circle, #FF6B35 1px, transparent 1px)',
              backgroundSize: '24px 24px',
              maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
            }}
          />

          {/* Rotating World-Record stamp (top-right) */}
          <motion.div
            aria-hidden
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-24 h-24 sm:w-32 sm:h-32 z-20"
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                <path
                  id="circle-path"
                  d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"
                />
              </defs>
              <text fill="#FF6B35" fontSize="8" fontWeight="700" letterSpacing="3.5">
                <textPath href="#circle-path" startOffset="0">
                  WORLD RECORD · 1989 · ZAGREB · SKEET OLYMPIQUE ·
                </textPath>
              </text>
            </svg>
            {/* Center medal emblem */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8F4F] flex items-center justify-center shadow-[0_0_20px_rgba(255,107,53,0.5)]">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} fill="white" />
            </div>
          </motion.div>

          {/* Photo container with glow */}
          <div className="relative w-full max-w-md mx-auto">
            {/* Radial spotlight behind photo */}
            <div
              aria-hidden
              className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_40%,rgba(255,107,53,0.3),transparent_70%)] blur-3xl scale-150"
            />

            <motion.div
              initial={{ scale: 0.92 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <img
                src={champion.photo}
                alt={champion.name}
                data-testid={`champion-${champion.id}-photo`}
                className="relative w-full h-auto select-none pointer-events-none"
                style={{
                  filter:
                    'drop-shadow(0 30px 60px rgba(0,0,0,0.55)) drop-shadow(0 10px 30px rgba(255,107,53,0.25))',
                }}
                draggable={false}
              />
            </motion.div>

            {/* Floating "FRANCE" + year chip (top-left of photo) */}
            <motion.div
              initial={{ opacity: 0, x: -20, y: -10 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-8 -left-4 sm:-left-8 flex flex-col gap-2 z-10"
            >
              <div className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-[#151515]/95 backdrop-blur-sm px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.6)]">
                <span className="text-base">🇫🇷</span>
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/80">FRANCE</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-[#FF6B35]/30 bg-[#FF6B35]/[0.08] backdrop-blur-sm px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.6)]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#FF6B35]">{champion.year}</span>
              </div>
            </motion.div>

            {/* Floating scorecard chip (right side of photo) */}
            <motion.div
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-1/2 -right-2 sm:-right-6 -translate-y-1/2 rounded-2xl border border-white/10 bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] px-4 py-3 shadow-[0_20px_40px_rgba(0,0,0,0.7)] z-10"
            >
              <div className="text-[9px] uppercase tracking-[0.2em] text-white/50 font-bold">Accuracy</div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl sm:text-3xl font-black text-[#FF6B35] font-['Poppins'] tabular-nums">98</span>
                <span className="text-sm font-bold text-white/60">%</span>
              </div>
              <div className="mt-1 flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="w-2 h-1 rounded-full bg-[#FF6B35]" />
                ))}
              </div>
            </motion.div>

            {/* Floating diploma thumbnail */}
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -10 }}
              whileInView={{ opacity: 1, y: 0, rotate: -6 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ rotate: -2, scale: 1.05 }}
              className="absolute -bottom-6 -left-4 sm:-left-14 w-32 sm:w-44 bg-[#151515] border border-white/10 rounded-xl p-2 shadow-[0_25px_60px_-12px_rgba(0,0,0,0.9)] z-10"
              data-testid={`champion-${champion.id}-diploma`}
            >
              <img
                src={champion.diploma}
                alt={`Diplôme ${champion.name}`}
                className="w-full h-auto rounded-md"
              />
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-[#FF6B35] font-bold mt-1.5 text-center">
                Diploma of Honour · {champion.year}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* RIGHT: Content */}
        <div className="order-1 lg:order-2 space-y-6">
          {/* Badge row */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#FF6B35]/30 bg-[#FF6B35]/10 px-3.5 py-1.5"
          >
            <Trophy className="w-3.5 h-3.5 text-[#FF6B35]" strokeWidth={2.5} />
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B35]">
              Ambassadeur ClayPulse
            </span>
          </motion.div>

          {/* Name */}
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            data-testid={`champion-${champion.id}-name`}
            className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Poppins'] tracking-tight text-white leading-[1.05]"
          >
            {champion.name}
          </motion.h3>

          {/* Titles */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap gap-2"
          >
            {champion.titles.map((t, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs sm:text-sm text-white/80 font-medium"
              >
                <Medal className="w-3.5 h-3.5 text-[#FF6B35]" strokeWidth={2.5} />
                {t}
              </span>
            ))}
          </motion.div>

          {/* Record mega-display */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="pt-2"
          >
            <div className="flex items-baseline gap-4">
              <BigCounter target={champion.record.value} total={champion.record.total} />
            </div>
            <div className="mt-2 text-xs sm:text-sm text-[#A0A0A0] uppercase tracking-widest font-semibold">
              {champion.event}
            </div>
          </motion.div>

          {/* Quote */}
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.55 }}
            data-testid={`champion-${champion.id}-quote`}
            className="relative pl-6 border-l-2 border-[#FF6B35]/50 py-2"
          >
            <Quote
              className="absolute -left-3 -top-1 w-6 h-6 text-[#FF6B35]/30"
              strokeWidth={2}
              fill="currentColor"
            />
            <p className="text-lg sm:text-xl text-white/85 font-['Manrope'] leading-relaxed italic">
              {champion.quote}
            </p>
            <footer className="mt-3 text-sm text-[#A0A0A0]">
              — {champion.name}, {champion.discipline}
            </footer>
          </motion.blockquote>
        </div>
      </div>
    </div>
  );
}

export default function Champions() {
  return (
    <section
      id="champions"
      data-testid="champions-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Background ambience */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D] via-[#121212] to-[#0D0D0D]"
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #FF6B35 1px, transparent 1px), radial-gradient(circle at 80% 70%, #FF6B35 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] animate-pulse" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-white/70">
              Ils préconisent ClayPulse
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Poppins'] tracking-tight text-white">
            Recommandé par les <span className="gradient-text">champions</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#A0A0A0] max-w-2xl mx-auto font-['Manrope']">
            Les plus grands tireurs utilisent et recommandent ClayPulse pour
            structurer leur entraînement et analyser leurs performances.
          </p>
        </motion.div>

        {/* Champions cards */}
        <div className="space-y-32">
          {champions.map((c, i) => (
            <ChampionCard champion={c} index={i} key={c.id} />
          ))}
        </div>
      </div>
    </section>
  );
}
